import type { Maintenance, Position } from '@/lib/traccar-api';

export type MaintenanceStatus = 'ok' | 'dueSoon' | 'overdue' | 'done' | 'unknown';

export interface MaintenanceProgress {
  status: MaintenanceStatus;
  /** Current odometer (m), engine hours (ms) or time (ms), in Traccar units. */
  current: number | null;
  /** Next due value, in Traccar units. */
  due: number | null;
  /** due - current; negative when overdue. */
  remaining: number | null;
}

const DAY = 24 * 60 * 60 * 1000;

// Warn this far ahead when the reminder has no period to take 10% of.
const DEFAULT_WARNING: Record<string, number> = {
  totalDistance: 1000 * 1000, // 1,000 km
  hours: 25 * 3600 * 1000, // 25 h
  date: 14 * DAY, // 14 days
};

export const getLinkedDeviceId = (maintenance: Maintenance): number | null => {
  const value = maintenance.attributes?.deviceId;
  return typeof value === 'number' ? value : value ? Number(value) : null;
};

export const getCurrentValue = (type: string, position?: Position | null): number | null => {
  if (type === 'date') return Date.now();
  const value = position?.attributes?.[type];
  return typeof value === 'number' ? value : null;
};

/**
 * Traccar fires a maintenance event each time the value crosses `start + n * period`.
 * Registering a service moves `start` to the next due point and sets `lastServiceAt`,
 * so from then on `start` is the explicit due value and passing it means overdue
 * (a serviced one-off reminder, with no period, is done).
 * Reminders never serviced through the app are treated as recurring: the next due
 * point is the upcoming `start + n * period`.
 */
export const getMaintenanceProgress = (maintenance: Maintenance, position?: Position | null): MaintenanceProgress => {
  const current = getCurrentValue(maintenance.type, position);
  if (current == null) return { status: 'unknown', current: null, due: null, remaining: null };

  const { start, period } = maintenance;
  const serviced = Boolean(maintenance.attributes?.lastServiceAt);
  // A one-off reminder (no period) that has been serviced is complete.
  if (serviced && period <= 0) return { status: 'done', current, due: null, remaining: null };
  let due = start;
  if (period > 0 && !serviced && current > start) {
    due = start + Math.ceil((current - start) / period) * period;
  }

  const remaining = due - current;
  const warning = period > 0 ? period * 0.1 : DEFAULT_WARNING[maintenance.type] ?? 0;
  const status: MaintenanceStatus = remaining < 0 ? 'overdue' : remaining <= warning ? 'dueSoon' : 'ok';
  return { status, current, due, remaining };
};

/** The Traccar-unit value a service resets from: odometer (km→m), engine hours (h→ms) or date. */
export const serviceValueFor = (type: string, service: { odometer_km?: number | null; engine_hours?: number | null; performed_at: string }) => {
  if (type === 'totalDistance') return service.odometer_km != null ? service.odometer_km * 1000 : null;
  if (type === 'hours') return service.engine_hours != null ? service.engine_hours * 3600 * 1000 : null;
  if (type === 'date') return new Date(service.performed_at).getTime();
  return null;
};

export const SERVICE_TYPES = [
  'oilChange',
  'oilFilter',
  'airFilter',
  'fuelFilter',
  'cabinFilter',
  'tires',
  'tireRotation',
  'alignment',
  'brakes',
  'battery',
  'coolant',
  'transmission',
  'timingBelt',
  'suspension',
  'electrical',
  'airConditioning',
  'inspection',
  'generalService',
] as const;

/** Preset service types are stored as keys and shown translated; anything else is free text. */
export const isPresetServiceType = (value: string) => (SERVICE_TYPES as readonly string[]).includes(value);
